// Full-fidelity loading-diagram renderer for the Load Board — mirrors the normal
// Container Planner diagram exactly (centre-channel side clearance, empty-floor bands,
// headroom, bracing flags, dimension callouts, rotation & stack markers).
import { Boxes } from "lucide-react";

// GLOBAL, DETERMINISTIC per-SKU colour — identical to the Container Planner + backend PDF/Excel
// so a product code shows ONE colour everywhere (FNV-1a hash of the code -> curated palette index).
// Edit-proof: adding/removing a pallet never re-colours another product.
const SKU_PALETTE = [
  "#2563eb", "#e11d48", "#16a34a", "#ea580c", "#7c3aed", "#0891b2",
  "#db2777", "#a16207", "#4338ca", "#0d9488", "#b91c1c", "#65a30d",
  "#9333ea", "#0284c7", "#c2410c", "#15803d", "#be185d", "#1e40af",
  "#047857", "#a21caf", "#854d0e", "#0e7490", "#4d7c0f", "#6d28d9",
  "#dc2626", "#d97706", "#059669", "#7e22ce", "#0369a1", "#ca8a04",
  "#be123c", "#3730a3", "#166534", "#831843",
];
function _skuHash(code) {
  const s = String(code).toUpperCase();
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  return h >>> 0;
}
function colorForCode(code) {
  if (!code) return "#64748b";
  return SKU_PALETTE[_skuHash(code) % SKU_PALETTE.length];
}

// Collision-resolved, deterministic, edit-stable per-SKU colour map (see ContainerPlanner.jsx).
export function buildColorMap(placement) {
  const codes = [];
  (placement || []).forEach(p => {
    (p.layer_products && p.layer_products.length ? p.layer_products : [p.product_code]).forEach(code => { if (code) codes.push(code); });
  });
  const uniq = Array.from(new Set(codes)).sort();
  const n = SKU_PALETTE.length;
  const used = new Set();
  const map = {};
  for (const code of uniq) {
    const start = _skuHash(code) % n;
    let picked = -1;
    for (let i = 0; i < n; i++) { const idx = (start + i) % n; if (!used.has(idx)) { picked = idx; break; } }
    if (picked < 0) picked = start;
    used.add(picked);
    map[code] = SKU_PALETTE[picked];
  }
  return map;
}

const _esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const _codeLabelSvg = (code, cx, cy, boxW, fill = "#fff", maxFont = 12, minFont = 6) => {
  const c = _esc(code);
  const avail = Math.max(8, boxW - 4);
  let fs = Math.min(maxFont, avail / (code.length * 0.62));
  if (fs >= minFont) {
    return `<text x="${cx}" y="${cy}" text-anchor="middle" fill="${fill}" font-size="${fs.toFixed(1)}" font-weight="700">${c}</text>`;
  }
  let sp = code.lastIndexOf("-", Math.ceil(code.length / 2));
  if (sp <= 0) sp = code.indexOf("-", Math.ceil(code.length / 2));
  if (sp <= 0) sp = Math.ceil(code.length / 2) - 1;
  const l1 = code.slice(0, sp + 1), l2 = code.slice(sp + 1);
  const longest = Math.max(l1.length, l2.length);
  const fs2 = Math.max(minFont, Math.min(maxFont, avail / (longest * 0.62)));
  return `<text x="${cx}" y="${(cy - fs2 * 0.55).toFixed(1)}" text-anchor="middle" fill="${fill}" font-size="${fs2.toFixed(1)}" font-weight="700">${_esc(l1)}</text>`
    + `<text x="${cx}" y="${(cy + fs2 * 0.6).toFixed(1)}" text-anchor="middle" fill="${fill}" font-size="${fs2.toFixed(1)}" font-weight="700">${_esc(l2)}</text>`;
};

function _dimBand(x0, x1, yTop, yBot, label, color = "#334155") {
  const w = x1 - x0;
  if (w < 3) return "";
  const yMid = (yTop + yBot) / 2;
  const a = Math.min(9, w / 2);
  const head = (px, dir) => `<path d="M${px},${yMid} L${px + dir * a},${yMid - 4.5} L${px + dir * a},${yMid + 4.5} Z" fill="${color}"/>`;
  const fs = 11;
  const tw = label ? label.length * fs * 0.62 : 0;
  let g = `<g>`;
  if (label && w > tw + 2 * a + 8) {
    const cx = (x0 + x1) / 2, gapL = cx - tw / 2 - 3, gapR = cx + tw / 2 + 3;
    g += `<line x1="${x0}" y1="${yMid}" x2="${gapL}" y2="${yMid}" stroke="${color}" stroke-width="1.2"/>`;
    g += `<line x1="${gapR}" y1="${yMid}" x2="${x1}" y2="${yMid}" stroke="${color}" stroke-width="1.2"/>`;
    g += head(x0, 1) + head(x1, -1);
    g += `<text x="${cx}" y="${yMid + fs * 0.35}" text-anchor="middle" fill="${color}" font-size="${fs}" font-weight="700">${_esc(label)}</text>`;
  } else {
    g += `<line x1="${x0}" y1="${yMid}" x2="${x1}" y2="${yMid}" stroke="${color}" stroke-width="1.2"/>`;
    g += head(x0, 1) + head(x1, -1);
    if (label) {
      const cx = (x0 + x1) / 2, fsN = 10, hh = fsN * 0.72, tw2 = label.length * fsN * 0.62;
      g += `<g transform="rotate(-90 ${cx} ${yMid})">`;
      g += `<rect x="${cx - tw2 / 2 - 2}" y="${yMid - hh}" width="${tw2 + 4}" height="${hh * 2}" fill="#ffffff" fill-opacity="0.9"/>`;
      g += `<text x="${cx}" y="${yMid + fsN * 0.35}" text-anchor="middle" fill="${color}" font-size="${fsN}" font-weight="700">${_esc(label)}</text>`;
      g += `</g>`;
    }
  }
  return g + `</g>`;
}

// "pinned / Xin overhang" badge — a longer/heavier pallet caps a shorter one below,
// weighing the short pallet down (no bracing needed). Uses backend pinned/top_overhang_in
// when present, else derives it from the per-layer footprints (floor -> top).
export function pinInfo(p) {
  let over = (p && p.top_overhang_in != null) ? p.top_overhang_in : 0;
  let pinned = !!(p && p.pinned);
  const fps = (p && p.layer_footprints_in) || [];
  if (fps.length > 1) {
    for (let i = 1; i < fps.length; i++) {
      const b = fps[i - 1] || [0, 0], c = fps[i] || [0, 0];
      const ov = Math.max(((c[0] || 0) - (b[0] || 0)) / 2, ((c[1] || 0) - (b[1] || 0)) / 2);
      if (ov > 0.1) { pinned = true; over = Math.max(over, Math.round(ov * 10) / 10); }
    }
  }
  return { pinned, over: Math.round(over * 10) / 10 };
}

function _pinBadgeSvg(p, x, y0, w, h) {
  const { pinned, over } = pinInfo(p);
  if (!pinned || w <= 30 || h <= 12) return "";
  const label = over > 0.1 ? `\u21ca ${over}"` : "\u21ca pinned";
  const bw = Math.min(w - 4, 10 + label.length * 5.6), bh = 12;
  const bx = x + w - bw - 2, by = y0 + h - bh - 2;
  let g = `<g><title>${_esc(`Pinned — a longer/heavier pallet caps the shorter one below (up to ${over} in overhang), weighing it down. No bracing needed.`)}</title>`;
  g += `<rect x="${bx}" y="${by}" width="${bw}" height="${bh}" rx="3" fill="#4338ca" fill-opacity="0.92" stroke="#ffffff" stroke-width="0.6"/>`;
  g += `<text x="${bx + bw / 2}" y="${by + bh - 3}" text-anchor="middle" fill="#ffffff" font-size="8.5" font-weight="800">${label}</text>`;
  return g + `</g>`;
}


export function topdownSvg(placement, cmap, cl, cw, opts = {}) {
  const LEN = 1000, sx = LEN / cl, sy = 140 / (cw || 1), TOL = 2;
  let s = `<rect x="0" y="5" width="${LEN}" height="140" fill="#f8fafc" stroke="#334155" stroke-width="1.5"/>`;
  s += `<line x1="0" y1="75" x2="${LEN}" y2="75" stroke="#cbd5e1" stroke-width="1"/>`;
  s += `<line x1="500" y1="5" x2="500" y2="145" stroke="#94a3b8" stroke-width="1" stroke-dasharray="6 4"/>`;
  s += `<line x1="600" y1="5" x2="600" y2="145" stroke="#f59e0b" stroke-width="1.5" stroke-dasharray="6 4"/>`;
  if (placement.length) {
    const minStart = Math.min(...placement.map(p => p.start_in));
    const maxEnd = Math.max(...placement.map(p => p.end_in));
    const frontGap = Math.round(minStart * 10) / 10;
    const rearGap = Math.round((cl - maxEnd) * 10) / 10;
    const gapBand = (x0, wPx, gapIn, side) => {
      if (wPx < 3 || gapIn <= 0.5) return "";
      const x1 = x0 + wPx;
      let g = `<g><title>${_esc(`${gapIn} in of empty floor at the ${side} of the container`)}</title>`;
      g += `<rect x="${x0}" y="5" width="${wPx}" height="140" fill="#e2e8f0" fill-opacity="0.5" stroke="#94a3b8" stroke-width="0.8" stroke-dasharray="5 3"/>`;
      g += _dimBand(x0 + 2, x1 - 2, 8, 142, `${gapIn}" empty`);
      return g + `</g>`;
    };
    s += gapBand(0, frontGap * sx, frontGap, "front (nose)");
    s += gapBand(maxEnd * sx, rearGap * sx, rearGap, "rear (doors)");
  }
  placement.forEach((p) => {
    const x = p.start_in * sx, w = Math.max(3, (p.end_in - p.start_in) * sx);
    const col = cmap[p.product_code] || "#64748b";
    const laneCapPx = 140;
    const crossPx = Math.max(3, Math.min(laneCapPx, p.cross_in ? p.cross_in * sy : laneCapPx));
    const y0 = p.lane === "A" ? 145 - crossPx : 5;
    const h = crossPx;
    const latIn = p.lateral_play_in != null ? p.lateral_play_in : 0;
    const ori = p.full_width ? "Full-width" : (p.rotated ? "Rotated 90\u00b0 (breadthwise)" : "Lengthwise");
    const lps = (p.layer_products && p.layer_products.length) ? p.layer_products : [p.product_code];
    const lfp = p.layer_footprints_in || [];
    const lh = p.layer_heights_in || [];
    const layerLines = lps.map((c, i) => {
      const fp = lfp[i] ? `${lfp[i][0]}\u00d7${lfp[i][1]}` : "";
      const hh = lh[i] != null ? `\u00d7${lh[i]}` : "";
      return `  L${i + 1} ${c}: ${fp}${hh} in`;
    }).join("\n");
    const head = p.headroom_in != null ? `\nHeadroom left on top: ${p.headroom_in} in` : "";
    const brace = p.needs_bracing ? `\n\u26a0 Needs bracing${latIn > TOL ? ` \u00b7 ${latIn} in side clearance` : ""}${p.overhang_in > TOL ? ` \u00b7 ${p.overhang_in} in on-stack overhang` : ""}` : "";
    const tip = `${p.product_code} \u00b7 ${ori} \u00b7 placed ${p.along_in}\u00d7${p.cross_in} in \u00b7 ${p.layers} layer(s) \u00b7 stack H ${p.total_height_in} in \u00b7 ${Math.round(p.weight_kg)}kg${p.added ? " \u00b7 FILLER (non-PO)" : ""}${head}${brace}\nLayers (floor\u2192top):\n${layerLines}`;
    s += `<g style="cursor:pointer" data-lane="${p.lane}" data-pos="${p.arr_index != null ? p.arr_index : ''}"><title>${_esc(tip + "\n\n\u21bb Click the corner badge (or double-click the pallet) to rotate the whole stack 90\u00b0")}</title>`;
    if (p.layers > 1) s += `<rect x="${x + 6}" y="${y0 - 6}" width="${w}" height="${h}" fill="#cbd5e1"/>`;
    const stroke = p.added ? "#f97316" : "#fff";
    const dash = p.added ? ` stroke-dasharray="5 3"` : "";
    s += `<rect x="${x}" y="${y0}" width="${w}" height="${h}" fill="${col}" stroke="${stroke}" stroke-width="${p.added ? 2.6 : 1.5}"${dash}/>`;
    if (w > 34 && h > 12) {
      if (p.layers > 1) s += `<text x="${x + w / 2}" y="${y0 + h / 2 + 4}" text-anchor="middle" fill="#fff" font-size="12" font-weight="700">\u00d7${p.layers}</text>`;
      else s += _codeLabelSvg(p.product_code, x + w / 2, y0 + h / 2 + 4, w);
    }
    // ── Label corners (fixed to avoid overlap between FILLER and ROTATED) ──
    //   Top-Right    → orange FILLER badge         (kept out of the way)
    //   Bottom-Right → high-contrast ROTATED sign  (always visible over any pallet colour)
    if (p.needs_bracing) s += `<rect x="${x + 1}" y="${y0 + 1}" width="${Math.max(1, w - 2)}" height="${Math.max(1, h - 2)}" fill="none" stroke="#ea580c" stroke-width="1.6" stroke-dasharray="3 2"/>`;
    s += _pinBadgeSvg(p, x, y0, w, h);
    if (p.added) {
      if (w > 52 && h > 16) {
        const bw = 46, bh = 14, bx = x + w - bw - 3, by = y0 + 3;
        s += `<rect x="${bx}" y="${by}" width="${bw}" height="${bh}" rx="3" fill="#f97316" stroke="#fff7ed" stroke-width="1"/>`;
        s += `<text x="${bx + bw / 2}" y="${by + bh - 3}" text-anchor="middle" fill="#ffffff" font-size="9.5" font-weight="800">FILLER</text>`;
      } else {
        const cx = x + w - 8, cy = y0 + 8;
        s += `<circle cx="${cx}" cy="${cy}" r="7" fill="#f97316" stroke="#fff7ed" stroke-width="1"/>`;
        s += `<text x="${cx}" y="${cy + 4}" text-anchor="middle" fill="#ffffff" font-size="12" font-weight="800">+</text>`;
      }
    }
    // Rotated badge — clickable pill anchored to the BOTTOM-RIGHT so it never
    // overlaps the top-right FILLER tag. Also acts as the click target for the
    // Sandbox 90° rotation (data-rotate-lane / data-rotate-pos). A subtle dark
    // pill with white ↻ is high contrast on every pallet colour.
    if (p.arr_index != null && w > 16 && h > 12) {
      const rot = !!p.rotated;
      const bw = Math.min(28, w - 4), bh = Math.min(14, h - 4);
      const bx = x + w - bw - 3, by = y0 + h - bh - 3;
      const fill = rot ? "#0f172a" : "#0f172a";
      const stroke2 = rot ? "#fde68a" : "#94a3b8";
      s += `<g data-rotate-lane="${_esc(p.lane)}" data-rotate-pos="${p.arr_index}" style="cursor:pointer"><title>${_esc(rot ? "Rotated 90\u00b0 (breadthwise) — click to un-rotate the entire stack" : "Rotate this ENTIRE stack 90\u00b0 (breadthwise). Recomputes footprint + axle balance instantly.")}</title>`;
      s += `<rect x="${bx}" y="${by}" width="${bw}" height="${bh}" rx="${bh / 2}" fill="${fill}" fill-opacity="0.86" stroke="${stroke2}" stroke-width="1.1"/>`;
      const lbl = rot ? "\u21bb ON" : "\u21bb";
      s += `<text x="${bx + bw / 2}" y="${by + bh - 3}" text-anchor="middle" fill="${rot ? "#fde68a" : "#ffffff"}" font-size="9.5" font-weight="800" style="pointer-events:none">${lbl}</text>`;
      s += `</g>`;
    }
    s += `</g>`;
  });
  // ---- combined lateral free play (centre channel between left & right rows) ----
  {
    const A = placement.filter((p) => p.lane === "A");
    const B = placement.filter((p) => p.lane === "B");
    const AB = placement.filter((p) => p.lane === "AB");
    const bounds = new Set([0, cl]);
    placement.forEach((p) => { bounds.add(Math.max(0, p.start_in)); bounds.add(Math.min(cl, p.end_in)); });
    const xs = [...bounds].filter((v) => v >= -0.01 && v <= cl + 0.01).sort((a, b) => a - b);
    const crossOf = (arr, mid) => { let c = 0; arr.forEach((p) => { if (p.start_in - 0.01 <= mid && p.end_in + 0.01 >= mid) c = Math.max(c, p.cross_in || 0); }); return c; };
    const segs = [];
    for (let i = 0; i < xs.length - 1; i++) {
      const x0 = xs[i], x1 = xs[i + 1];
      if (x1 - x0 < 0.5) { segs.push(null); continue; }
      const mid = (x0 + x1) / 2;
      if (AB.some((p) => p.start_in - 0.01 <= mid && p.end_in + 0.01 >= mid)) { segs.push(null); continue; }
      const ca = crossOf(A, mid), cb = crossOf(B, mid);
      if (ca === 0 && cb === 0) { segs.push(null); continue; }
      const gap = Math.round((cw - ca - cb) * 10) / 10;
      if (gap <= TOL) { segs.push(null); continue; }
      segs.push({ x0, x1, ca, cb, gap });
    }
    const merged = [];
    segs.forEach((sg) => {
      if (!sg) { merged.push(null); return; }
      const last = merged[merged.length - 1];
      if (last && Math.abs(last.ca - sg.ca) < 0.6 && Math.abs(last.cb - sg.cb) < 0.6) last.x1 = sg.x1;
      else merged.push({ ...sg });
    });
    merged.forEach((sg) => {
      if (!sg) return;
      const px0 = sg.x0 * sx, px1 = sg.x1 * sx;
      const yTopBand = 5 + sg.cb * sy, yBotBand = 145 - sg.ca * sy;
      const bh = yBotBand - yTopBand;
      if (bh < 4 || px1 - px0 < 3) return;
      s += `<g><title>${_esc(`${sg.gap} in combined side clearance (both lanes) — brace this centre channel`)}</title>`;
      s += `<rect x="${px0}" y="${yTopBand}" width="${px1 - px0}" height="${bh}" fill="#fde68a" fill-opacity="0.6" stroke="#f59e0b" stroke-width="0.9" stroke-dasharray="5 3"/>`;
      if ((px1 - px0) > 46 && bh > 10) s += `<text x="${(px0 + px1) / 2}" y="${(yTopBand + yBotBand) / 2 + 3.5}" text-anchor="middle" fill="#b45309" font-size="10" font-weight="800">${sg.gap}" gap</text>`;
      else if (bh > 24 && (px1 - px0) > 16) { const cx = (px0 + px1) / 2, cy = (yTopBand + yBotBand) / 2; s += `<text x="${cx}" y="${cy + 3}" text-anchor="middle" fill="#b45309" font-size="9" font-weight="800" transform="rotate(-90 ${cx} ${cy})">${sg.gap}"</text>`; }
      s += `</g>`;
    });
  }
  const _cls = opts.svgClass || "w-full h-28 mb-3";
  return `<svg viewBox="0 0 1000 150" preserveAspectRatio="none" class="${_cls}" style="display:block">${s}</svg>`;
}

export function sideSvg(placement, cmap, laneId, cl, ch, opts = {}) {
  const LEN = 1000, sx = LEN / cl, yB = 105, yT = 5, sh = 100 / ch;
  let s = `<rect x="0" y="${yT}" width="${LEN}" height="${yB - yT}" fill="#f8fafc" stroke="#334155" stroke-width="1.5"/>`;
  if (placement.length) {
    const minStart = Math.min(...placement.map(p => p.start_in));
    const maxEnd = Math.max(...placement.map(p => p.end_in));
    const frontGap = Math.round(minStart * 10) / 10;
    const rearGap = Math.round((cl - maxEnd) * 10) / 10;
    const gapBand = (x0, wPx, gapIn, side) => {
      if (wPx < 3 || gapIn <= 0.5) return "";
      const x1 = x0 + wPx;
      let g = `<g><title>${_esc(`${gapIn} in of empty floor at the ${side} of the container`)}</title>`;
      g += `<rect x="${x0}" y="${yT}" width="${wPx}" height="${yB - yT}" fill="#e2e8f0" fill-opacity="0.5" stroke="#94a3b8" stroke-width="0.7" stroke-dasharray="4 3"/>`;
      g += _dimBand(x0 + 2, x1 - 2, yT + 3, yB - 3, `${gapIn}"`);
      return g + `</g>`;
    };
    s += gapBand(0, frontGap * sx, frontGap, "front (nose)");
    s += gapBand(maxEnd * sx, rearGap * sx, rearGap, "rear (doors)");
  }
  placement.filter((p) => p.lane === laneId || p.lane === "AB").forEach((p) => {
    const x = p.start_in * sx, w = Math.max(3, (p.end_in - p.start_in) * sx);
    const col = cmap[p.product_code] || "#64748b";
    const heights = (p.layer_heights_in && p.layer_heights_in.length)
      ? p.layer_heights_in : Array(p.layers).fill(p.height_in || ch / Math.max(1, p.layers));
    const lps = (p.layer_products && p.layer_products.length) ? p.layer_products : [p.product_code];
    const lfp = p.layer_footprints_in || [];
    const lal = p.layer_along_in || [];
    const la = p.layer_added || [];
    const isAdded = (i) => (la.length ? !!la[i] : !!p.added);
    const totalH = heights.reduce((a, b) => a + b, 0);
    const over = totalH > ch + 0.1;
    let cum = 0;
    for (let layer = 0; layer < heights.length; layer++) {
      const boxh = heights[layer] * sh;
      const yy = yB - (cum + heights[layer]) * sh;
      cum += heights[layer];
      if (yy > yB) continue;
      const yTop = Math.max(yT, yy);
      const dh = Math.min(boxh, yB - yTop) - 1;
      if (dh <= 0) continue;
      const code = lps[layer] || p.product_code;
      const lcol = cmap[code] || col;
      const added = isAdded(layer);
      const ll = p.layer_loose || [];
      const loose = ll.length ? !!ll[layer] : false;
      const lw = (lal[layer] != null && lal[layer] > 0) ? Math.max(3, lal[layer] * sx) : w;
      const belowLw = (layer > 0 && lal[layer - 1] != null && lal[layer - 1] > 0) ? Math.max(3, lal[layer - 1] * sx) : w;
      const lx = x;
      const fp = lfp[layer] ? ` \u00b7 ${lfp[layer][0]}\u00d7${lfp[layer][1]}\u00d7${heights[layer]} in` : "";
      s += `<g style="cursor:pointer"><title>${_esc(`${code} \u00b7 layer ${layer + 1} of ${heights.length}${fp}${added ? " \u00b7 FILLER (non-PO)" : ""}${loose ? " \u00b7 LOOSE \u2014 brace" : ""}`)}</title>`;
      const lstroke = over ? "#dc2626" : (added ? "#f97316" : (loose ? "#ea580c" : "#fff"));
      const ldash = (added || loose) ? ` stroke-dasharray="4 2"` : "";
      if (loose && (belowLw - lw) > 4) {
        s += `<rect x="${lx + lw}" y="${yTop}" width="${belowLw - lw}" height="${dh}" fill="#fde68a" fill-opacity="0.55" stroke="#f59e0b" stroke-width="0.7" stroke-dasharray="3 2"/>`;
        if ((belowLw - lw) > 24 && dh > 10) s += `<text x="${lx + lw + (belowLw - lw) / 2}" y="${yTop + dh / 2 + 3}" text-anchor="middle" fill="#b45309" font-size="8" font-weight="700">gap</text>`;
      }
      s += `<rect x="${lx}" y="${yTop}" width="${lw}" height="${dh}" fill="${lcol}" stroke="${lstroke}" stroke-width="${over ? 1.5 : (added ? 2.2 : (loose ? 1.6 : 0.8))}"${ldash}/>`;
      if (lw > 30 && dh > 11) {
        const codeY = added ? (yTop + dh - 3) : (yTop + dh / 2 + 3.5);
        s += _codeLabelSvg(code, lx + lw / 2, codeY, lw, "#fff", 9.5, 5.5);
      }
      if (added && dh > 6) {
        if (lw > 24 && dh > 9) {
          const label = lw > 44 ? "FILLER" : "F";
          const tw = Math.min(lw > 44 ? 34 : 14, lw - 3), th = Math.min(9, dh - 2);
          s += `<rect x="${lx + 1.5}" y="${yTop + 1}" width="${tw}" height="${th}" rx="1.5" fill="#f97316" stroke="#fff7ed" stroke-width="0.7"/>`;
          s += `<text x="${lx + 1.5 + tw / 2}" y="${yTop + 1 + th - 1.8}" text-anchor="middle" fill="#ffffff" font-size="6.5" font-weight="800">${label}</text>`;
        } else {
          s += `<circle cx="${lx + Math.min(lw / 2, 6)}" cy="${yTop + Math.min(dh / 2, 5)}" r="${Math.min(4, dh / 2 - 0.5)}" fill="#f97316" stroke="#fff7ed" stroke-width="0.7"/>`;
        }
      }
      if (opts.removable && lw > 16 && dh > 13) {
        const rxc = lx + lw - 8, ryc = yTop + 8;
        s += `<g data-remove-code="${_esc(code)}" data-remove-filler="${added ? "1" : "0"}" style="cursor:pointer"><title>${_esc(`Move this ${added ? "FILLER" : "PO"} pallet of ${code} to the Unassigned pool`)}</title>`;
        s += `<circle cx="${rxc}" cy="${ryc}" r="6.6" fill="#e11d48" stroke="#ffffff" stroke-width="1.1"/>`;
        s += `<path d="M${rxc - 2.6} ${ryc - 2.6} L${rxc + 2.6} ${ryc + 2.6} M${rxc + 2.6} ${ryc - 2.6} L${rxc - 2.6} ${ryc + 2.6}" stroke="#ffffff" stroke-width="1.5" stroke-linecap="round"/>`;
        s += `</g>`;
      }
      if (opts.auditable && lw > 16 && dh > 22) {
        const qx = lx + lw - 8, qy = yTop + dh - 8;
        s += `<g data-audit-code="${_esc(code)}" data-audit-lane="${_esc(p.lane === "B" ? "B" : "A")}" data-audit-base="${_esc(lps.join("|"))}" style="cursor:help"><title>${_esc(`Audit — can one more ${code} pallet sit on top of this ${lps.length}-high stack?`)}</title>`;
        s += `<circle cx="${qx}" cy="${qy}" r="6.6" fill="#2563eb" stroke="#ffffff" stroke-width="1.1"/>`;
        s += `<text x="${qx}" y="${qy + 3.3}" text-anchor="middle" fill="#ffffff" font-size="10" font-weight="800" style="pointer-events:none">?</text>`;
        s += `</g>`;
      }
      s += `</g>`;
    }
    if (!over && p.headroom_in != null && p.headroom_in > 1) {
      const topY = yB - totalH * sh;
      const bandH = topY - yT;
      if (bandH > 3) {
        s += `<g><title>${_esc(`${p.headroom_in} in of empty height above this stack`)}</title>`;
        s += `<rect x="${x}" y="${yT}" width="${w}" height="${bandH}" fill="none" stroke="#94a3b8" stroke-width="0.7" stroke-dasharray="3 2"/>`;
        if (w > 34 && bandH > 10) s += `<text x="${x + w / 2}" y="${yT + bandH / 2 + 3}" text-anchor="middle" fill="#94a3b8" font-size="8.5" font-weight="600">${p.headroom_in}" left</text>`;
        s += `</g>`;
      }
    }
  });
  const _cls = opts.svgClass || "w-full h-24";
  return `<svg viewBox="0 0 1000 115" preserveAspectRatio="none" class="${_cls}" style="display:block">${s}</svg>`;
}

export default function LoadDiagram({ plan, onRotateStack, cmap: cmapProp }) {
  if (!plan) return <div className="text-sm text-slate-400 p-4">Pick a container to see its loading diagram.</div>;
  const cmap = cmapProp || buildColorMap(plan.placement);
  const cont = plan.container || {};
  const cl = cont.internal_length_in || 1;
  const ch = cont.internal_height_in || 1;
  const cw = cont.internal_width_in || 1;
  const placement = plan.placement || [];
  const handleTopClick = (e) => {
    if (!onRotateStack) return;
    const g = e.target.closest("[data-lane]");
    if (!g) return;
    const lane = g.getAttribute("data-lane");
    const pos = g.getAttribute("data-pos");
    if (lane == null || pos === "" || pos == null) return;
    onRotateStack(lane, parseInt(pos, 10));
  };
  if (!placement.length) {
    return <div className="text-sm text-slate-500 p-4 border border-slate-200 rounded-lg" data-testid="board-load-diagram">Empty container — drag pallets here to load it.</div>;
  }
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white" data-testid="board-load-diagram">
      <div className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1.5"><Boxes className="w-4 h-4" />Loading diagram <span className="text-[11px] font-normal text-slate-400">(live)</span></div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 mb-2" data-testid="board-diagram-legend">
        {Object.entries(cmap).map(([code, colr]) => (
          <span key={code} className="inline-flex items-center gap-1 text-[11px] text-slate-600"><span className="w-3 h-3 rounded-sm inline-block" style={{ background: colr }} />{code}</span>
        ))}
      </div>
      <div className="text-[11px] text-slate-500 mb-1">Top-down floor plan <span className="text-blue-600 font-medium">(click a stack to rotate it 90°)</span> <span className="text-slate-400">(×N = stack layers; ↻ = rotated 90°; <span className="text-indigo-600 font-medium">indigo ⇊ badge = pinned (a longer/heavier pallet caps a shorter one below, weighing it down — no bracing)</span>; amber centre band = combined side clearance of BOTH lanes — slide risk, brace it; orange dashed = needs bracing; grey line = 50%, amber = 60% FHWA axle line; shaded end bands = empty floor)</span></div>
      <div className="flex items-stretch gap-1.5 mb-1">
        <div className="flex items-center justify-center"><span className="text-[10px] font-bold tracking-wider text-slate-600" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>◄ FRONT</span></div>
        <div className="flex-1">
          <div className="text-[10px] font-bold tracking-wider text-slate-500 text-center mb-0.5">RIGHT (FROM REAR)</div>
          <div onClick={handleTopClick} data-testid="board-topview-svg" dangerouslySetInnerHTML={{ __html: topdownSvg(placement, cmap, cl, cw) }} />
          <div className="text-[10px] font-bold tracking-wider text-slate-500 text-center mt-0.5">LEFT (FROM REAR)</div>
        </div>
        <div className="flex items-center justify-center"><span className="text-[10px] font-bold tracking-wider text-slate-600" style={{ writingMode: "vertical-rl" }}>REAR ►</span></div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
        <div>
          <div className="text-[11px] text-slate-500 mb-0.5">Side elevation — Right Lane (from Rear) <span className="text-slate-400">· dashed = headroom / bracing</span></div>
          <div dangerouslySetInnerHTML={{ __html: sideSvg(placement, cmap, "B", cl, ch) }} />
        </div>
        <div>
          <div className="text-[11px] text-slate-500 mb-0.5">Side elevation — Left Lane (from Rear)</div>
          <div dangerouslySetInnerHTML={{ __html: sideSvg(placement, cmap, "A", cl, ch) }} />
        </div>
      </div>
    </div>
  );
}
